import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import type { KeyboardEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Markdown } from './Markdown';
import { ReviewOption } from './ReviewOption';
import type { EpiClarifyingQuestion } from '../types/epi-chat-types';
import type { ReviewPick } from '../types/review-types';

interface ReviewQuestionStepProps {
  question: EpiClarifyingQuestion;
  questionNumber: number;
  questionCount: number;
  pick: ReviewPick | null;
  /** Free text typed but not yet sent; survives closing the card. */
  draft: string;
  disabled: boolean;
  onSelect: (pick: ReviewPick) => void;
  onDraftChange: (text: string) => void;
}

/** One clarifying question: its options, plus a text box for a free-form answer. */
export const ReviewQuestionStep = ({
  question,
  questionNumber,
  questionCount,
  pick,
  draft,
  disabled,
  onSelect,
  onDraftChange,
}: ReviewQuestionStepProps) => {
  const { formatMessage } = useIntl();
  const groupName = `review-${question.questionId}`;

  const sendDraft = () => {
    const text = draft.trim();
    if (text) onSelect({ type: 'custom', text });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendDraft();
    }
  };

  return (
    <>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HelpOutlineRoundedIcon
            sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }}
            aria-hidden
          />
          <Typography variant="caption" color="text.secondary">
            <FormattedMessage
              id="epiChat.review.questionPosition"
              defaultMessage="Question {current} of {total}"
              values={{ current: questionNumber, total: questionCount }}
            />
          </Typography>
        </Stack>
        <Box sx={{ fontWeight: 600 }}>
          <Markdown dense>{question.question}</Markdown>
        </Box>
      </Stack>

      <Stack spacing={2} role="radiogroup" aria-label={question.question}>
        {question.options.map((option) => (
          <ReviewOption
            key={option.label}
            name={groupName}
            label={option.label}
            selected={pick?.type === 'option' && pick.label === option.label}
            disabled={disabled}
            onSelect={() => onSelect({ type: 'option', label: option.label })}
          >
            {option.description && (
              <Typography variant="body2" color="text.secondary">
                {option.description}
              </Typography>
            )}
          </ReviewOption>
        ))}
      </Stack>

      {question.allowCustom && (
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            fullWidth
            size="small"
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={formatMessage({
              id: 'epiChat.review.customPlaceholder',
              defaultMessage: 'Type your answer',
            })}
            disabled={disabled}
            inputProps={{ 'aria-label': question.question, 'data-testid': 'review-custom-input' }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={sendDraft}
            disabled={disabled || !draft.trim()}
            data-testid="review-custom-send"
            sx={{ textTransform: 'none' }}
          >
            {formatMessage({ id: 'epiChat.review.send', defaultMessage: 'Send' })}
          </Button>
        </Stack>
      )}
    </>
  );
};
